import axios from "axios";
import type { AxiosError, AxiosResponse } from "axios";
import { AUTH_JWT_KEY } from "./localStorage";

export const setAuthToken = (token: string) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    axios.defaults.headers.common["Authorization"] = undefined;
  }
};

/** False for expired tokens and for malformed ones isTokenExpired cannot decode. */
const isUsableToken = (jwt: string): boolean => {
  try {
    return !isTokenExpired(jwt);
  } catch {
    return false;
  }
};

/**
 * Fallback that guarantees a stored session's JWT rides on EVERY request,
 * regardless of React effect ordering.
 *
 * setAuthToken() above is only called from App.tsx's mount effect - but
 * Router.tsx (a child of App) dispatches fetchSubscription in its own [jwt]
 * effect, and React runs child effects before parent effects. On every load
 * with a stored session, the first getSubscriptionState therefore left
 * without an Authorization header and 403'd (dashboard's "problème avec le
 * serveur" banner; WIXAR preprod incident of 2026-08-05).
 *
 * Installed from index.tsx at module scope - before React renders anything -
 * so no dispatch can outrun it. It only fills ABSENCE: an Authorization set
 * explicitly (setAuthToken's default, or a per-request header) always wins.
 * Logout is safe: authSlice clears localStorage, so there is nothing to
 * attach afterwards. Guarded by isTokenExpired to mirror App.tsx's own rule,
 * with malformed tokens ignored rather than thrown on.
 */
export const installAuthHeaderFallback = () => {
  axios.interceptors.request.use((config) => {
    if (!config.headers?.Authorization) {
      const jwt = localStorage.getItem(AUTH_JWT_KEY);
      if (jwt && isUsableToken(jwt)) {
        config.headers.Authorization = `Bearer ${jwt}`;
      }
    }
    return config;
  });
};

const isWrappedResponseData = (value: unknown): value is { data: unknown } =>
  typeof value === "object" && value !== null && "data" in value;

export const setAxiosResponseHandler = (alertCb: (response: AxiosResponse | undefined) => void) => {
  axios.interceptors?.response.use(
    (response) => {
      const responseData: unknown = response.data;
      if (isWrappedResponseData(responseData) && responseData.data !== undefined) {
        return { ...response, data: responseData.data };
      }
      return response;
    },
    (error: AxiosError) => {
      console.error("setAxiosResponseHandler Axios interceptor error: ", error);
      alertCb(error.response);
      throw error;
    },
  );
};

export const setAxiosRequestHandler = () => {
  axios.interceptors.request.use(
    (config) => {
      // Check if the method is POST or PUT
      if (config.method === "post" || config.method === "put") {
        const requestData: unknown = config.data;
        if (requestData && typeof requestData === "object") {
          const hasDataKey = Object.prototype.hasOwnProperty.call(requestData, "data");

          // Check if the payload is an object and not already wrapped with a "data" key
          if (!hasDataKey) {
            config.data = { data: requestData };
          }
        }
      }

      return config;
    },
    (error: AxiosError) => {
      console.error("setAxiosRequestHandler Axios interceptor error: ", error);

      // Do something with request error
      return Promise.reject(error);
    },
  );
};

const getExpClaim = (value: unknown): number | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const exp = (value as Record<string, unknown>).exp;
  return typeof exp === "number" ? exp : undefined;
};

export const isTokenExpired = (jwt: string): boolean => {
  const decodedToken: unknown = JSON.parse(window.atob(jwt.split(".")[1]));
  const exp = getExpClaim(decodedToken);
  const currentDate = new Date();

  if (exp === undefined) {
    return false;
  }
  if (exp * 1000 < currentDate.getTime()) {
    return true;
  }
  return false;
};
